/**
 * The route tree (spec §14.1).
 *
 * Code-based rather than file-based: file-based routing needs the TanStack
 * Router Vite plugin and a generated `routeTree.gen.ts`, and a generated file
 * cannot be built inside a test. The shape is identical — one module per route,
 * `beforeLoad` guards, zod-validated search params — so the Phase 3 editor can
 * switch to the plugin without moving any screen.
 *
 * Guards are in `beforeLoad` ONLY, as §14.1 requires: a component never decides
 * whether the user is allowed to be here.
 */
import {
  createRootRouteWithContext,
  createRoute,
  redirect,
  Outlet,
} from "@tanstack/react-router";
import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { z } from "zod";
import type { VenueT } from "@madaline/shared";
import type { MadalineApi } from "../app/api.js";
import { ensureMe, isOnboarded, onboardingStep, ONBOARDING_STEPS } from "../app/session.js";
import { queryKeys, STALE } from "../app/query-client.js";
import type { WizardStepKey as WizardStep } from "../features/models/draft.js";
import type { SETTINGS_SECTIONS } from "../features/settings/SettingsScreens.js";
import { lazyScreen, screen } from "./lazy.js";

export interface RouterContext {
  api: MadalineApi;
  queryClient: QueryClient;
}

// ---------------------------------------------------------------------------
// Screens loaded on demand
// ---------------------------------------------------------------------------

/**
 * STATIC ABOVE, `import()` BELOW — AND THE LINE BETWEEN THEM IS THE BUNDLE.
 *
 * What is imported statically at the top of this file is in the entry chunk,
 * which every visitor downloads before anything is drawn: the router, the
 * guards and the session, and nothing a screen draws with.
 *
 * Everything else is a chunk fetched by the route that draws it. Until
 * 2026-10-13 it was all static and the build was ONE 2,016 kB file (579 kB
 * gzipped, ~45% of it Blockly) served to a stranger reading the landing page.
 *
 * ADDING A SCREEN: declare it here with `lazyScreen`, and name it in the
 * route's `screen(…)` so the router has the chunk in memory before the first
 * render (see `./lazy.tsx`). A screen imported statically instead still works
 * and still passes every behavioural test — which is why
 * `test/route-splitting.test.ts` and `scripts/bundle-budget.mjs` exist.
 */
// The public funnel. Off the entry chunk since the design system landed: each
// of these screens carries a stylesheet of its own, and a stylesheet imported
// statically from here is one every route pays for (`entryCss` in
// scripts/bundle-budget.mts). The router has the chunk in memory before the
// first render (`screen(…)` below), so nothing flashes.
const Landing = lazyScreen(() => import("../features/public/Landing.js"), "Landing");
const auth = () => import("../features/auth/AuthScreens.js");
const LoginScreen = lazyScreen(auth, "LoginScreen");
const SignupScreen = lazyScreen(auth, "SignupScreen");
const legal = () => import("../features/public/PublicScreens.js");
const LegalIndex = lazyScreen(legal, "LegalIndex");
const LegalDoc = lazyScreen(legal, "LegalDoc");
const DocsPageView = lazyScreen(() => import("./docs-screens.js"), "DocsPageView");
const LearnExampleFrame = lazyScreen(() => import("./docs-screens.js"), "LearnExampleFrame");
const ExampleViewer = lazyScreen(
  () => import("../features/learn/ExampleViewer.js"),
  "ExampleViewer",
);
// The guide for AI assistants, drawn for people. The same words are served as
// text at /llms.txt and /llms-full.txt (files in public/, not routes).
const AiGuidePage = lazyScreen(() => import("../features/ai-guide/AiGuidePage.js"), "AiGuidePage");
const Onboarding = lazyScreen(() => import("../features/onboarding/Onboarding.js"), "Onboarding");
// The pages a mailed link lands on. Public, and off the entry chunk: nobody
// reading the landing page needs the reset form.
const recovery = () => import("../features/auth/RecoveryScreens.js");
const ForgotPasswordScreen = lazyScreen(recovery, "ForgotPasswordScreen");
const ResetPasswordScreen = lazyScreen(recovery, "ResetPasswordScreen");
const VerifyEmailScreen = lazyScreen(recovery, "VerifyEmailScreen");
const AppShell = lazyScreen(() => import("../features/shell/AppShell.js"), "AppShell");
const Dashboard = lazyScreen(() => import("../features/dashboard/Dashboard.js"), "Dashboard");
const NewStrategy = lazyScreen(() => import("../features/dashboard/NewStrategy.js"), "NewStrategy");
const Workspace = lazyScreen(() => import("../features/workspace/Workspace.js"), "Workspace");
const RunPage = lazyScreen(() => import("../features/run/RunPage.js"), "RunPage");
// The lists and the account-level libraries: every noun in the rail has an
// address of its own (docs/design/reference/ia/IA.md §2).
const StrategiesPage = lazyScreen(
  () => import("../features/strategies/StrategiesPage.js"),
  "StrategiesPage",
);
const RunsPage = lazyScreen(() => import("../features/runs/RunsPage.js"), "RunsPage");
const ModelsLibrary = lazyScreen(() => import("../features/library/ModelsLibrary.js"), "ModelsLibrary");
const ApisLibrary = lazyScreen(() => import("../features/library/ApisLibrary.js"), "ApisLibrary");

const settings = () => import("../features/settings/SettingsScreens.js");
const SettingsLayout = lazyScreen(settings, "SettingsLayout");
const ProfileSettings = lazyScreen(settings, "ProfileSettings");
const SecuritySettings = lazyScreen(settings, "SecuritySettings");
const CredentialsSettings = lazyScreen(settings, "CredentialsSettings");
const ExecutionSettings = lazyScreen(settings, "ExecutionSettings");
const RiskSettings = lazyScreen(settings, "RiskSettings");
const LegalSettings = lazyScreen(settings, "LegalSettings");
const PlanSettings = lazyScreen(settings, "PlanSettings");
const JurisdictionSettings = lazyScreen(settings, "JurisdictionSettings");
const AppearanceSettings = lazyScreen(settings, "AppearanceSettings");
const AccountSettings = lazyScreen(settings, "AccountSettings");
// Public: the session is gone by the time it is drawn.
const AccountDeleted = lazyScreen(() => import("../features/settings/AccountDeleted.js"), "AccountDeleted");

/**
 * The venue search param.
 *
 * Declared here rather than imported as `Venue` from `@madaline/shared` because
 * that pulls the AST module — and node:crypto with it — into the browser
 * bundle. `venuesCoverTheAst` is the guard that keeps the two in step: it stops
 * compiling the moment the AST's `Venue` enum gains a member this list is
 * missing.
 */
const VENUES = ["kalshi", "polymarket"] as const;
const venuesCoverTheAst: Exclude<VenueT, (typeof VENUES)[number]> extends never
  ? true
  : never = true;
void venuesCoverTheAst;
const VenueParam = z.enum(VENUES);

/**
 * The QueryClientProvider lives here rather than around `<RouterProvider>` so
 * the router is self-contained: guards, loaders and components all read the one
 * client that was handed to `createMadalineRouter`, and a test mounts the app
 * with a single element.
 */
const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: function Root() {
    const { queryClient } = rootRoute.useRouteContext();
    return (
      <QueryClientProvider client={queryClient}>
        <Outlet />
      </QueryClientProvider>
    );
  },
});

// ---------------------------------------------------------------------------
// Public
// ---------------------------------------------------------------------------

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: screen(function LandingRoute() {
    return <Landing />;
  }, Landing),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: z.object({ next: z.string().optional() }),
  component: screen(function LoginRoute() {
    const { api } = loginRoute.useRouteContext();
    return <LoginScreen api={api} />;
  }, LoginScreen),
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  // `/signup?invite=K7M2Q-…` is the link the operator's `invite create` prints.
  validateSearch: z.object({ invite: z.string().optional().catch(undefined) }),
  component: screen(function SignupRoute() {
    const { api } = signupRoute.useRouteContext();
    return <SignupScreen api={api} />;
  }, SignupScreen),
});

/**
 * Where a mailed link lands, and where a forgotten password starts.
 *
 * PUBLIC, like `/login`: a reset is asked for by somebody who cannot sign in,
 * and a "confirm your email" link is opened from a mail client that holds no
 * cookie. The API builds the reset link as `{PUBLIC_URL}/reset-password?token=`
 * and Better Auth redirects a verification to `/verify-email[?error=]`; until
 * these three routes existed both arrived at "That page does not exist."
 */
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: screen(function ForgotPasswordRoute() {
    const { api } = forgotPasswordRoute.useRouteContext();
    return <ForgotPasswordScreen api={api} />;
  }, ForgotPasswordScreen),
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  validateSearch: z.object({ token: z.string().optional().catch(undefined) }),
  component: screen(function ResetPasswordRoute() {
    const { api } = resetPasswordRoute.useRouteContext();
    return <ResetPasswordScreen api={api} />;
  }, ResetPasswordScreen),
});

const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verify-email",
  validateSearch: z.object({ error: z.string().optional().catch(undefined) }),
  component: screen(function VerifyEmailRoute() {
    const { api } = verifyEmailRoute.useRouteContext();
    return <VerifyEmailScreen api={api} />;
  }, VerifyEmailScreen),
});

/**
 * The receipt for a deleted account. Public, because `DELETE /me` signs the
 * person out as it answers: the receipt is drawn from that response, on a page
 * that needs no session (`features/settings/AccountDeleted.tsx`).
 */
const accountDeletedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account-deleted",
  component: screen(function AccountDeletedRoute() {
    return <AccountDeleted />;
  }, AccountDeleted),
});

const legalIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/legal",
  component: screen(function LegalIndexRoute() {
    return <LegalIndex />;
  }, LegalIndex),
});

const legalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/legal/$doc",
  component: screen(function LegalRoute() {
    const { doc } = legalRoute.useParams();
    return <LegalDoc doc={doc} />;
  }, LegalDoc),
});

/**
 * Two statements with addresses of their own, because those are the
 * addresses people look for: `/security` is the Policy line of
 * `/.well-known/security.txt` (RFC 9116), and `/accessibility` is where an
 * accessibility statement is expected. Both are documents of the legal corpus
 * (`packages/shared/src/legal/security.md`, `accessibility.md`), drawn by the
 * same reader, and also reachable under `/legal/`.
 */
const securityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/security",
  component: screen(function SecurityRoute() {
    return <LegalDoc doc="security" />;
  }, LegalDoc),
});

const accessibilityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/accessibility",
  component: screen(function AccessibilityRoute() {
    return <LegalDoc doc="accessibility" />;
  }, LegalDoc),
});

/**
 * The public documentation (`/docs`, `/docs/*`).
 *
 * PUBLIC AND GUARD-FREE, ON PURPOSE. It is declared here beside `/` and
 * `/login`, outside the `/app` subtree, with no `beforeLoad` and no
 * `ensureMe` — because documentation behind a login cannot answer the question
 * "what is this?", which is the question a stranger arrives with.
 * `test/public-copy.test.tsx` mounts every one of these pages against an API
 * that answers 401 and asserts they render, so the guarantee is a failing test
 * rather than a comment.
 *
 * One splat route serves the whole corpus rather than one `createRoute` per
 * page: `DOCS_PAGES` is the registry, and a page absent from it has no route.
 * The registry test asserts the converse — that no page in it is unreachable.
 *
 * The index and the splat are CHILDREN of one `/docs` route, not siblings at
 * the root. As siblings the matcher let the splat — whose tail may be empty —
 * answer "/docs", and every link to the home page warned that it had asked for
 * one route and been served by another (`test/docs-housekeeping.test.tsx`).
 */
const docsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/docs",
  component: Outlet,
});

const docsIndexRoute = createRoute({
  getParentRoute: () => docsRoute,
  path: "/",
  component: screen(function DocsIndexRoute() {
    return <DocsPageView path="/docs" />;
  }, DocsPageView),
});

const docsPageRoute = createRoute({
  getParentRoute: () => docsRoute,
  path: "$",
  component: screen(function DocsSplatRoute() {
    const { _splat } = docsPageRoute.useParams();
    return <DocsPageView path={`/docs/${_splat ?? ""}`} />;
  }, DocsPageView),
});

/**
 * The read-only worked example (Phase 3, Task 8).
 *
 * Public and guard-free: it teaches what the blocks do and contains no account
 * data. It is deliberately NOT a template — the page has no affordance to copy
 * or start a strategy from what it shows, because supplying a user with
 * trading operations to begin with would be recommending a strategy rather
 * than providing a platform (spec §2, CFTC Reg 4.14(a)(9)).
 *
 * Two chunks, loaded together: the docs frame (`./docs-screens.tsx`, which
 * says why the page is folded into the docs) and the viewer, which is the one
 * public screen that draws real blocks and so the one that needs Blockly.
 */
const learnExampleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/learn/example",
  component: screen(
    function LearnExampleRoute() {
      return (
        <LearnExampleFrame>
          <ExampleViewer />
        </LearnExampleFrame>
      );
    },
    LearnExampleFrame,
    ExampleViewer,
  ),
});

/**
 * `/ai-guide` — the guide for AI assistants (`features/ai-guide`).
 *
 * Public and guard-free, like `/docs`: an assistant helping somebody decide
 * whether to sign up has to be able to read it, and so does the person. Its
 * text is also served as `/llms.txt` and `/llms-full.txt`, which a fetcher
 * that does not run JavaScript can read; `test/ai-guide.test.tsx` keeps the
 * three in step.
 */
const aiGuideRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ai-guide",
  component: screen(function AiGuideRoute() {
    return <AiGuidePage />;
  }, AiGuidePage),
});

// ---------------------------------------------------------------------------
// Onboarding (session required, onboarding not)
// ---------------------------------------------------------------------------

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  validateSearch: z.object({
    step: z.enum(ONBOARDING_STEPS).default("region"),
    venue: VenueParam.optional(),
  }),
  beforeLoad: async ({ context, location }) => {
    const me = await ensureMe(context.queryClient, context.api);
    if (!me) throw redirect({ to: "/login", search: { next: location.href } });
    // The SERVER says this account has finished: there is nothing here to
    // answer again, and the last step would only re-open the first strategy.
    if (me.profile.onboarding_state === "done") throw redirect({ to: "/app" });
    return { me };
  },
  component: screen(function OnboardingRoute() {
    const { api, me } = onboardingRoute.useRouteContext();
    const { step, venue } = onboardingRoute.useSearch();
    return <Onboarding api={api} me={me} step={step} venue={venue} />;
  }, Onboarding),
});

// ---------------------------------------------------------------------------
// The signed-in app
// ---------------------------------------------------------------------------

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app",
  beforeLoad: async ({ context, location }) => {
    const me = await ensureMe(context.queryClient, context.api);
    if (!me) throw redirect({ to: "/login", search: { next: location.href } });
    if (!isOnboarded(me)) {
      throw redirect({ to: "/onboarding", search: { step: onboardingStep(me) } });
    }
    return { me };
  },
  component: screen(function AppRoute() {
    const { api } = appRoute.useRouteContext();
    return <AppShell api={api} />;
  }, AppShell),
});

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  // §14.1: "loaders ensureQueryData the critical queries".
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: queryKeys.strategies,
      queryFn: () => context.api.listStrategies(),
      staleTime: STALE.strategies,
    }),
  component: screen(function DashboardRoute() {
    const { api } = dashboardRoute.useRouteContext();
    return <Dashboard api={api} />;
  }, Dashboard),
});

const newStrategyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/strategies/new",
  component: screen(function NewStrategyRoute() {
    const { api } = newStrategyRoute.useRouteContext();
    return <NewStrategy api={api} />;
  }, NewStrategy),
});

const workspaceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/strategies/$strategyId",
  validateSearch: z.object({
    // §14.1 spells the workspace tabs as path segments
    // (/app/strategies/:id/(strategy|models|apis)). Phase 2 has only the
    // strategy tab, so it is a search param until models and APIs exist.
    tab: z.enum(["strategy", "models", "apis"]).default("strategy"),
    // `problems` is what the API's `compiles` and `warnings` checks link to
    // (`fix_url: "?panel=problems"`). `.catch`: a panel this build has never
    // heard of opens the workspace without one — the alternative, which is
    // what happened, is zod refusing the whole route and "Something went
    // wrong!" where the strategy should be.
    panel: z.enum(["assistant", "console", "inspector", "problems"]).optional().catch(undefined),
    run: z.string().optional(),
    block: z.string().optional(),
    // §14.1's `/app/strategies/:id/models/:modelId?tab=` and the wizard's
    // `?step=`, addressable so a half-finished model is a link.
    model: z.string().optional(),
    step: z
      .enum(["create", "data", "features", "parameters", "train", "evaluate", "activate"])
      .optional(),
  }),
  component: screen(function WorkspaceRoute() {
    const { api } = workspaceRoute.useRouteContext();
    const { strategyId } = workspaceRoute.useParams();
    const { tab, model, step, panel } = workspaceRoute.useSearch();
    return (
      <Workspace
        api={api}
        strategyId={strategyId}
        tab={tab}
        {...(model === undefined ? {} : { modelId: model })}
        {...(step === undefined ? {} : { modelStep: step })}
        {...(panel === undefined ? {} : { panel })}
      />
    );
  }, Workspace),
});

/**
 * `/app/strategies` — every strategy, as a list you act on.
 *
 * A sibling of `/strategies/new` and `/strategies/$strategyId`; the router
 * ranks a static segment above a parameter, so neither shadows the other.
 */
const strategiesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/strategies",
  component: screen(function StrategiesRoute() {
    const { api } = strategiesRoute.useRouteContext();
    return <StrategiesPage api={api} />;
  }, StrategiesPage),
});

/**
 * `/app/runs` — what is running, and the history.
 *
 * The filters are in the address so that a filtered history is a link (the
 * LIVE band's "and 2 more live runs", a strategy's "its runs"). Each `.catch`
 * is the lesson of `?panel=` above: a value this build has never heard of
 * opens the page unfiltered rather than refusing the whole route.
 */
const runsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/runs",
  validateSearch: z.object({
    state: z.enum(["all", "running", "ended"]).optional().catch(undefined),
    mode: z.enum(["all", "paper", "live"]).optional().catch(undefined),
    strategy: z.string().optional().catch(undefined),
  }),
  component: screen(function RunsRoute() {
    const { api } = runsRoute.useRouteContext();
    const { state, mode, strategy } = runsRoute.useSearch();
    const navigate = runsRoute.useNavigate();
    return (
      <RunsPage
        api={api}
        filters={{ state: state ?? "all", mode: mode ?? "all", strategy }}
        onFiltersChange={(next) =>
          void navigate({
            // The defaults stay out of the address: `/app/runs` is "everything".
            search: {
              ...(next.state === "all" ? {} : { state: next.state }),
              ...(next.mode === "all" ? {} : { mode: next.mode }),
              ...(next.strategy === undefined ? {} : { strategy: next.strategy }),
            },
            replace: true,
          })
        }
      />
    );
  }, RunsPage),
});

/**
 * `/app/models` and `/app/apis` — the account-level libraries.
 *
 * These two addresses are what six preflight checks answer as their fix link
 * (`credentials`, `credential_live_eligible`, `data_sources`,
 * `api_version_current`, `models`, and the browser's own
 * `kalshi_paper_best_effort`). They were "That page does not exist.", then a
 * redirect to the most recently edited strategy's tab, because MODELS and APIS
 * were tabs on one strategy and not places of their own. They are places now
 * (IA §4, "Models and APIs are a library"): the venue keys every one of those
 * checks is about are on `/app/apis`, and every model on the account — shared
 * or one strategy's — is on `/app/models`. The tabs inside a strategy are
 * still there, as a filter on the same library.
 *
 * `test/fix-links.test.tsx` reads the API's source for every link it emits
 * and loads each one through this tree.
 */
const modelsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/models",
  // The wizard's own addressing, exactly as on a strategy's MODELS tab.
  validateSearch: z.object({
    model: z.string().optional().catch(undefined),
    step: z
      .enum(["create", "data", "features", "parameters", "train", "evaluate", "activate"])
      .optional()
      .catch(undefined),
  }),
  component: screen(function ModelsRoute() {
    const { api } = modelsRoute.useRouteContext();
    const { model, step } = modelsRoute.useSearch();
    const navigate = modelsRoute.useNavigate();
    // A stable identity: the wizard reports its address from an effect, and a
    // callback rebuilt on every render would hand it a new function on every
    // frame of the navigation it causes (see `ModelsTab`).
    const onNavigate = useCallback(
      (next: { model: string | null; step: WizardStep | null }) =>
        void navigate({
          search: {
            ...(next.model === null ? {} : { model: next.model }),
            ...(next.step === null ? {} : { step: next.step }),
          },
          replace: true,
        }),
      [navigate],
    );
    return <ModelsLibrary api={api} modelId={model} step={step} onNavigate={onNavigate} />;
  }, ModelsLibrary),
});

const apisRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/apis",
  // The builder's own addressing: `?api=<id>` (or `new`) opens it, `strategy`
  // is the APIs tab it was opened from, `step` is where it stands.
  validateSearch: z.object({
    api: z.string().optional().catch(undefined),
    strategy: z.string().optional().catch(undefined),
    step: z.enum(["request", "test", "fields", "blocks"]).optional().catch(undefined),
  }),
  component: screen(function ApisRoute() {
    const { api } = apisRoute.useRouteContext();
    const { api: apiId, strategy, step } = apisRoute.useSearch();
    const navigate = apisRoute.useNavigate();
    const onNavigate = useCallback(
      (search: { api?: string; strategy?: string; step?: "request" | "test" | "fields" | "blocks" }) =>
        void navigate({ search, replace: true }),
      [navigate],
    );
    return <ApisLibrary api={api} apiId={apiId} strategyId={strategy} step={step} onNavigate={onNavigate} />;
  }, ApisLibrary),
});

/** `fix_url: "/app/disclosures"` (the `disclosures` check) — the acceptance screen. */
const disclosuresShortcutRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/disclosures",
  beforeLoad: () => {
    throw redirect({ to: "/app/settings/legal" });
  },
});

const runRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/runs/$runId",
  component: screen(function RunRoute() {
    const { api } = runRoute.useRouteContext();
    const { runId } = runRoute.useParams();
    return <RunPage api={api} runId={runId} />;
  }, RunPage),
});

// ---------------------------------------------------------------------------
// Settings (§14.6)
// ---------------------------------------------------------------------------

/**
 * One route per §14.6 section, each mounting the component Phase 4 built.
 *
 * They are real paths rather than a `?section=` param because the API links to
 * them: `assertDisclosuresAccepted` refuses a run with
 * `fix_url: "/app/settings/legal"`, and the LIVE refusal points at
 * `/settings/execution`. A fix link that 404s is worse than no link, so both
 * spellings resolve — the second through the redirects below.
 */
const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings",
  // The layout reads `me` for the plan's name and the two-factor mark in its nav.
  component: screen(function SettingsLayoutRoute() {
    const { api } = settingsRoute.useRouteContext();
    return <SettingsLayout api={api} />;
  }, SettingsLayout),
});

const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/app/settings/profile" });
  },
});

const settingsProfileRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "profile",
  component: screen(function SettingsProfileRoute() {
    const { api } = settingsProfileRoute.useRouteContext();
    return <ProfileSettings api={api} />;
  }, ProfileSettings),
});

const settingsSecurityRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "security",
  component: screen(function SettingsSecurityRoute() {
    const { api } = settingsSecurityRoute.useRouteContext();
    return <SecuritySettings api={api} />;
  }, SecuritySettings),
});

const settingsCredentialsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "credentials",
  component: screen(function SettingsCredentialsRoute() {
    const { api } = settingsCredentialsRoute.useRouteContext();
    return <CredentialsSettings api={api} />;
  }, CredentialsSettings),
});

const settingsExecutionRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "execution",
  component: screen(function SettingsExecutionRoute() {
    const { api } = settingsExecutionRoute.useRouteContext();
    return <ExecutionSettings api={api} />;
  }, ExecutionSettings),
});

const settingsRiskRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "risk",
  component: screen(function SettingsRiskRoute() {
    const { api } = settingsRiskRoute.useRouteContext();
    return <RiskSettings api={api} />;
  }, RiskSettings),
});

const settingsLegalRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "legal",
  component: screen(function SettingsLegalRoute() {
    const { api } = settingsLegalRoute.useRouteContext();
    return <LegalSettings api={api} />;
  }, LegalSettings),
});

/**
 * `/app/settings/jurisdiction` and `/app/settings/plan` — two addresses the
 * API links to (`geo`, `us_paper_only`; `entitlement`). They were redirects
 * to Profile, which could state the facts but change neither; each is now a
 * section of its own — re-attesting, and the plan's limits as meters.
 */
const settingsJurisdictionRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "jurisdiction",
  component: screen(function SettingsJurisdictionRoute() {
    const { api } = settingsJurisdictionRoute.useRouteContext();
    return <JurisdictionSettings api={api} />;
  }, JurisdictionSettings),
});

const settingsPlanRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "plan",
  component: screen(function SettingsPlanRoute() {
    const { api } = settingsPlanRoute.useRouteContext();
    return <PlanSettings api={api} />;
  }, PlanSettings),
});

/** This browser's display settings: the block palette. */
const settingsAppearanceRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "appearance",
  component: screen(function SettingsAppearanceRoute() {
    return <AppearanceSettings />;
  }, AppearanceSettings),
});

/** Export my data, and delete my account (track S2). */
const settingsAccountRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "account",
  component: screen(function SettingsAccountRoute() {
    const { api } = settingsAccountRoute.useRouteContext();
    return <AccountSettings api={api} />;
  }, AccountSettings),
});

// ---------------------------------------------------------------------------
// Plan-compatibility redirects
// ---------------------------------------------------------------------------

/**
 * The Phase 2 plan writes the dashboard as `/strategies` and the workspace as
 * `/strategies/:id`; §14.1 puts both under `/app`. The spec wins for the real
 * routes, and these two redirects keep every link in the plan working.
 */
const legacyStrategiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/strategies",
  beforeLoad: () => {
    throw redirect({ to: "/app" });
  },
});

const legacyStrategyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/strategies/$strategyId",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/app/strategies/$strategyId",
      params: { strategyId: params.strategyId },
    });
  },
});

/**
 * `/settings/*` without the `/app` prefix — the spelling the run route's LIVE
 * refusal uses for its fix link. An unknown section lands on Profile rather
 * than on "that page does not exist".
 */
type SettingsPath = (typeof SETTINGS_SECTIONS)[number]["to"];

/**
 * Spelled out rather than derived from `SETTINGS_SECTIONS` at run time: that
 * constant lives beside the settings screens, and importing it as a value put
 * every settings screen in the entry chunk for the sake of six strings. The
 * import above is type-only, and `settingsPathsCoverTheSections` stops
 * compiling the moment the nav gains a section this list is missing.
 */
const SETTINGS_PATHS = [
  "/app/settings/profile",
  "/app/settings/security",
  "/app/settings/credentials",
  "/app/settings/execution",
  "/app/settings/risk",
  "/app/settings/legal",
  "/app/settings/plan",
  "/app/settings/jurisdiction",
  "/app/settings/appearance",
  "/app/settings/account",
] as const satisfies readonly SettingsPath[];
const settingsPathsCoverTheSections: Exclude<
  SettingsPath,
  (typeof SETTINGS_PATHS)[number]
> extends never
  ? true
  : never = true;
void settingsPathsCoverTheSections;

const SETTINGS_BY_SECTION: Record<string, SettingsPath> = Object.fromEntries(
  SETTINGS_PATHS.map((to) => [to.slice("/app/settings/".length), to]),
);

const legacySettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  beforeLoad: () => {
    throw redirect({ to: "/app/settings/profile" });
  },
});

const legacySettingsSectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/$section",
  beforeLoad: ({ params }) => {
    throw redirect({ to: SETTINGS_BY_SECTION[params.section] ?? "/app/settings/profile" });
  },
});

export const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  verifyEmailRoute,
  accountDeletedRoute,
  legalIndexRoute,
  legalRoute,
  securityRoute,
  accessibilityRoute,
  learnExampleRoute,
  aiGuideRoute,
  docsRoute.addChildren([docsIndexRoute, docsPageRoute]),
  onboardingRoute,
  appRoute.addChildren([
    dashboardRoute,
    strategiesRoute,
    newStrategyRoute,
    workspaceRoute,
    runsRoute,
    runRoute,
    modelsRoute,
    apisRoute,
    disclosuresShortcutRoute,
    settingsRoute.addChildren([
      settingsIndexRoute,
      settingsProfileRoute,
      settingsSecurityRoute,
      settingsCredentialsRoute,
      settingsExecutionRoute,
      settingsRiskRoute,
      settingsLegalRoute,
      settingsAppearanceRoute,
      settingsAccountRoute,
      settingsJurisdictionRoute,
      settingsPlanRoute,
    ]),
  ]),
  legacyStrategiesRoute,
  legacyStrategyRoute,
  legacySettingsRoute,
  legacySettingsSectionRoute,
]);
